import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { BladeStackProvider } from './BladeStackProvider';
import { BladeDataProvider, type BladeDataSource } from './BladeDataSource';
import { BladeActionProvider, type BladeActionSource } from './BladeActionSource';
import { liveSource } from './sources/liveSource';
import { liveActionSource } from './sources/liveActionSource';
import { WorkbenchShell } from './WorkbenchShell';
import { AuditDataProvider, type AuditDataSource } from '../audit/AuditDataSource';
import { auditSource } from '../audit/sources/auditSource';
import { AUDIT_DEFAULT_STACK } from '../audit/stack';
import { useBladeStackUrl } from './useBladeStackUrl';
import { decodeStack } from './url';
import type { BladeStackState } from './types';

// URL-synced, live-data AR Workbench shell (HUB-275 deep-link + HUB-277 binding).
// Mounted at /w/*: the stack is seeded from the URL on first load, kept in sync
// with the address bar via useBladeStackUrl, and its primitives bind to the live
// @hub/api source. Refresh-safe and shareable. Runs under the app's
// QueryClientProvider (main.tsx). `source` is injectable for tests.

// Landing view when the URL carries no stack.
//
// The evaluation queue, not the AR worklist. /w is now the app's front door
// (see App.tsx), and the AR worklist binds the WORKQUEUE service — unrelated to
// the eval pilot and empty in that deployment, so landing there showed an empty
// list to every reviewer and auditor who signed in. The eval batches primitive
// is scope-aware on the server: an auditor sees every batch, a reviewer sees
// the batches their queue names.
const DEFAULT_ROUTE_STACK: BladeStackState = AUDIT_DEFAULT_STACK;

function ShellWithUrlSync() {
  useBladeStackUrl();
  // The audit nav belongs on the AUTHENTICATED route, not only on the
  // backendless preview. Without it the coding-review workflow — batch queue,
  // case compare, program metrics — exists in the bundle but is unreachable by
  // anyone who signs in, which is indistinguishable from not having shipped it.
  return <WorkbenchShell enableAuditNav />;
}

export function WorkbenchShellRoute({
  source = liveSource,
  actionSource = liveActionSource,
  audit = auditSource,
}: {
  source?: BladeDataSource;
  actionSource?: BladeActionSource;
  /** audit data seam — the live evalreview.v1 source when VITE_AUDIT_LIVE is set,
   *  the static mock otherwise (see audit/sources/auditSource). */
  audit?: AuditDataSource;
} = {}) {
  const location = useLocation();
  // Decode the stack once on mount; later URL changes flow through useBladeStackUrl.
  const initial = useMemo(() => {
    const decoded = decodeStack(location.pathname, location.search);
    return decoded.blades.length ? decoded : DEFAULT_ROUTE_STACK;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BladeDataProvider source={source}>
      <BladeActionProvider source={actionSource}>
        <AuditDataProvider source={audit}>
          <BladeStackProvider initial={initial}>
            <ShellWithUrlSync />
          </BladeStackProvider>
        </AuditDataProvider>
      </BladeActionProvider>
    </BladeDataProvider>
  );
}
